import { FunctionComponent, ComponentPropsWithoutRef, SVGProps } from "react";
import { ReactComponent as Spinner } from "assets/spinner.svg";
import classNames from "classnames";

export type Variant =
  | "Primary"
  | "Dark"
  | "Secondary"
  | "Red"
  | "Delete"
  | "Transparent"
  | "Success"
  | "Subtle"
  | "Outlined";
type Size = "md" | "sm" | "xs";

interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: Variant;
  isFullWidth?: boolean;
  isLoading?: boolean;
  LeftIcon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  size?: Size;
}

export const Button = ({
  children,
  variant = "Primary",
  isFullWidth = false,
  isLoading = false,
  LeftIcon,
  size = "md",
  disabled,
  ...props
}: ButtonProps) => {
  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      type="button"
      className={classNames(
        "select-none box-border rounded-md inline-flex items-center justify-center whitespace-nowrap align-middle cursor-pointer uppercase",
        {
          "bg-primary-500 hover:bg-primary-600 text-white fill-white":
            variant === "Primary",
          "bg-primary-500/20 hover:bg-primary-600/30 text-brand fill-brand":
            variant === "Subtle",
          "bg-red-600 hover:bg-red-800 text-white fill-white":
            variant === "Red",
          "bg-white border-2 border-red-600 hover:bg-red-100 text-red-600 fill-red-600 leading-tight":
            variant === "Delete",
          "border-2 border-primary-600 hover:bg-primary-100 text-primary-600 fill-primary-600 leading-tight":
            variant === "Secondary",
          "border-2 border-primary-500 hover:bg-primary-100 text-primary-500 fill-transparent leading-tight":
            variant === "Outlined",
          "bg-primary-900 hover:bg-primary-800 text-white fill-white":
            variant === "Dark",
          "w-full": isFullWidth,
          "text-sm py-2.5 px-5": size === "md",
          "text-xs py-2 px-3": size === "sm",
          "text-xs py-1 px-2": size === "xs",
          "bg-primary-600/50 hover:bg-primary-500/50 cursor-not-allowed":
            variant === "Primary" && disabled,
          "bg-primary-100/30 border-primary-600/10 text-primary-600/30 hover:bg-primary-100/20 cursor-not-allowed":
            variant === "Secondary" && disabled,
          "bg-red-200/0 border-2 border-red-600/10 hover:bg-red-100/20 text-red-600/30 cursor-not-allowed":
            variant === "Delete" && disabled,
          "bg-red-600/50 hover:bg-red-600/50 cursor-not-allowed":
            variant === "Red" && disabled,
          "bg-primary-900 hover:bg-primary-800 cursor-not-allowed":
            variant === "Dark" && disabled,
          "text-gray-300": variant === "Transparent" && disabled,
          "bg-green-400 text-white": variant === "Success",
        }
      )}
    >
      {(LeftIcon || isLoading) && (
        <span
          className={classNames("inline-flex self-center w-5 h-5 shrink-0", {
            "mr-1": children !== undefined,
            "w-5 h-5": size === "md",
            "w-4 h-4": size === "sm",
            "w-3 h-3": size === "xs",
          })}
        >
          {isLoading ? (
            <Spinner
              className={classNames(
                "text-primary-400 animate-spin fill-white",
                {
                  "text-primary-200 fill-primary-600": variant === "Secondary",
                  "w-5 h-5": size === "md",
                  "w-4 h-4": size === "sm",
                  "w-3 h-3": size === "xs",
                }
              )}
            />
          ) : (
            LeftIcon && <LeftIcon className="h-full" aria-hidden />
          )}
        </span>
      )}
      {children}
    </button>
  );
};
